import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import type { ProjectStats } from "@/hooks/useProjectStats";
import type { OrgGovernanceRollup } from "@/types/executive";

export function useOrgGovernancePulse(slugs: string[]): {
  rollup: OrgGovernanceRollup;
  loading: boolean;
} {
  const results = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: queryKeys.projects.stats(slug),
      queryFn: () => api<ProjectStats>(`/api/v2/projects/${slug}/stats`),
      enabled: slug.length > 0,
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const rollup = useMemo((): OrgGovernanceRollup => {
    if (slugs.length === 0) {
      return { score: null, pass_count: 0, fail_count: 0, pending_count: 0, coverage_pct: null };
    }

    let pass = 0, fail = 0, pending = 0, covSum = 0, covCount = 0;
    for (const r of results) {
      if (!r.data) continue;
      pass += r.data.gate_pass_rate.pass_count;
      fail += r.data.gate_pass_rate.fail_count;
      pending += r.data.gate_pass_rate.pending_count;
      covSum += r.data.artifact_coverage_pct;
      covCount++;
    }
    const score = pass + fail > 0 ? (pass / (pass + fail)) * 100 : null;
    return {
      score,
      pass_count: pass,
      fail_count: fail,
      pending_count: pending,
      coverage_pct: covCount > 0 ? covSum / covCount : null,
    };
  }, [results, slugs.length]);

  return {
    rollup,
    loading: results.some((r) => r.isLoading),
  };
}
